import { isNullish } from "@bodil/core/assert";
import { attribute, Component, reactive, type Deps } from "@bodil/dom/component";
import { animationFrame } from "@bodil/dom/dom";
import type { Placement } from "@floating-ui/utils";
import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";

import { KeyboardCommand, keymapLookup } from "./keymap";
import { BtkPopup } from "./popup";

import styles from "./dropdown.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-dropdown": BtkDropdown;
    }
}

@customElement("btk-dropdown")
export class BtkDropdown extends Component {
    static deps: Deps = [BtkPopup];

    @attribute accessor placement: Placement = "bottom-start";
    @attribute accessor sync: BtkPopup["sync"] | null = null;
    @attribute({ type: Boolean }) accessor open = false;
    @attribute({ type: Boolean }) accessor disabled = false;
    @attribute({ type: Boolean }) accessor contextMenu = false;
    @attribute({ type: Boolean }) accessor anchorMouse = false;

    private get triggerElements(): Array<HTMLElement> {
        return this.querySlot({ slot: "trigger", flatten: true, instanceOf: HTMLElement });
    }

    private contextMenuButton?: number;

    @reactive menuTemplate?: () => unknown;
    @reactive private menuTemplateActive = false;
    @reactive private mouseAttachment?: { x: number; y: number };

    private readonly popup = createRef<BtkPopup>();
    private readonly popupSlot = createRef<HTMLSlotElement>();

    private get popupElements(): Array<HTMLElement> {
        // TODO should replace with this.querySlot(), yes?
        const slot = this.popupSlot.value;
        if (slot === undefined) {
            return [];
        }
        const els = [...slot.assignedElements({ flatten: true }), ...slot.children].filter(
            (el) => el instanceof HTMLElement,
        );
        return els;
    }

    async show(): Promise<void> {
        if (this.disabled) {
            return;
        }
        if (this.menuTemplate !== undefined && !this.menuTemplateActive) {
            this.menuTemplateActive = true;
            await this.updateComplete;
        }
        return this.popup.value?.show();
    }

    hide(): Promise<void> {
        return this.popup.value?.hide() ?? Promise.resolve();
    }

    toggle(): Promise<void> {
        if (this.disabled) {
            return Promise.resolve();
        }
        return this.popup.value?.toggle() ?? Promise.resolve();
    }

    focusOnTrigger() {
        this.triggerElements[0]?.focus();
    }

    protected override render(): unknown {
        return html`
            <btk-popup
                ${ref(this.popup)}
                .placement=${this.placement}
                .anchor=${this.mouseAttachment !== undefined
                    ? {
                          getBoundingClientRect: () =>
                              new DOMRect(this.mouseAttachment!.x, this.mouseAttachment!.y),
                      }
                    : undefined}
                sync=${ifDefined(this.sync)}
                flip
                shift
                @toggle=${this.handlePopupToggled}
                @btk-selected=${this.hide}
                @btk-cancelled=${this.hide}
                ><slot
                    name="trigger"
                    slot="anchor"
                    @click=${this.handleTriggerClick}
                    @contextmenu=${this.handleTriggerContextMenu}
                    @mouseup=${this.handleTriggerMouseUp}
                    @keydown=${this.handleTriggerKeyDown}
                    @slotchange=${this.updateAccessibleTrigger}
                ></slot
                >${when(
                    this.menuTemplate !== undefined,
                    () =>
                        this.menuTemplateActive
                            ? html`<div class="popup-template" @keydown=${this.handleKeyDown}>
                                  ${this.menuTemplate!()}
                              </div>`
                            : nothing,
                    () => html`<slot ${ref(this.popupSlot)} @keydown=${this.handleKeyDown}></slot>`,
                )}</btk-popup
            >
        `;
    }

    protected override updated() {
        super.updated();
        this.updateAccessibleTrigger();
    }

    private handleTriggerClick(e: PointerEvent) {
        if (this.disabled) {
            e.stopPropagation();
            return;
        }
        if (this.contextMenu) {
            return;
        }
        const path = e.composedPath();
        for (const trigger of this.triggerElements) {
            if (path.includes(trigger)) {
                e.preventDefault();
                e.stopPropagation();
                if (!this.open) {
                    if (this.anchorMouse) {
                        this.mouseAttachment = e;
                    }
                    void this.show();
                }
                break;
            }
        }
    }

    private handleTriggerContextMenu(e: PointerEvent) {
        if (this.disabled) {
            return;
        }
        if (this.contextMenu) {
            // This is hairy, but if the contextmenu event was triggered by
            // pressing Ctrl+F10 or whatever the context menu key is defined as,
            // event.button will be either 0, meaning the primary button, on
            // Firefox, or -1 on Chrome, but if the event was triggered by a
            // right click, it'll be 2 as expected. I can't see any other
            // difference between the events, so let's go with it.
            if (e.button <= 0) {
                if (this.anchorMouse) {
                    this.mouseAttachment = e;
                }
                void this.show();
            } else {
                this.contextMenuButton = e.button;
            }
            e.preventDefault();
            e.stopPropagation();
        }
    }

    private handleTriggerMouseUp(e: MouseEvent) {
        if (this.contextMenu && e.button === this.contextMenuButton && !this.open) {
            if (this.anchorMouse) {
                this.mouseAttachment = e;
            }
            void this.show();
        }
        this.contextMenuButton = undefined;
    }

    private handleTriggerKeyDown(e: KeyboardEvent) {
        if (this.disabled) {
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        const cmd = keymapLookup(e);
        if (cmd === (this.contextMenu ? KeyboardCommand.Menu : KeyboardCommand.Enter)) {
            e.preventDefault();
            void this.toggle();
        }
    }

    private handlePopupToggled(e: ToggleEvent) {
        this.open = e.newState === "open";
        const trigger = this.triggerElements[0];
        if (!isNullish(trigger)) {
            trigger.classList.toggle("btk-dropdown-open", this.open);
        }
        if (e.oldState === "closed" && e.newState === "open") {
            (this.popup.value?.focusFirstCandidate() ?? false) || this.focusOnTrigger();
        }
    }

    private handleKeyDown(event: KeyboardEvent) {
        if (this.disabled) {
            event.stopPropagation();
            event.preventDefault();
            return;
        }
        if (event.key === "Tab") {
            // Tabbing outside of the containing element closes the panel
            animationFrame(() => {
                const activeElement = document.activeElement;
                if (
                    activeElement?.closest(this.popup.value!.tagName.toLowerCase()) !== this.popup
                ) {
                    void this.hide();
                }
            });
        }
    }

    private updateAccessibleTrigger() {
        const target = this.triggerElements[0];
        if (!isNullish(target)) {
            target.setAttribute("aria-haspopup", "true");
            target.setAttribute("aria-expanded", this.open ? "true" : "false");
        }
    }

    static styles = styles;
}
